import React, { useCallback } from 'react';

import { css } from '@compiled/react';
import Slider from 'react-slick';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Picture } from '@/components/wpl/image/src/image';
import { ContentBlock } from '@/libraries/design/components/content-block/src/content-block';
import { Link } from '@/libraries/design/components/link/src/link';
import { Video } from '@/libraries/design/components/video/src/video';
import { wacToken } from '@/libraries/design/foundations';
import {
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
	WCL_GRID_MAX_WIDTH,
} from '@/libraries/design/foundations/grid/src/base';
import { RichTextComponent } from '@/libraries/design/utils';

import type { Settings as SlickSettings } from 'react-slick';

export interface FeaturesHighlightProps {
	title?: string;
	description?: string | React.ReactElement<typeof RichTextComponent>;
	linkText?: string;
	linkHref?: string;
	feature1Eyebrow?: string;
	feature1Title: string;
	feature1Body?: string | React.ReactElement<typeof RichTextComponent>;
	feature1ImageSrc: string;
	feature1ImageAltText?: string;
	feature2Eyebrow?: string;
	feature2Title: string;
	feature2Body?: string | React.ReactElement<typeof RichTextComponent>;
	feature2ImageSrc: string;
	feature2ImageAltText?: string;
	feature3Eyebrow?: string;
	feature3Title?: string;
	feature3Body?: string | React.ReactElement<typeof RichTextComponent>;
	feature3ImageSrc: string;
	feature3ImageAltText?: string;
	feature1VideoSrc?: string;
	feature1VideoAltText?: string;
	feature1VideoPoster?: string;
	feature2VideoSrc?: string;
	feature2VideoAltText?: string;
	feature2VideoPoster?: string;
	feature3VideoSrc?: string;
	feature3VideoAltText?: string;
	feature3VideoPoster?: string;
}
// eslint-disable-next-line complexity
export const FeaturesHighlight = (props: FeaturesHighlightProps) => {
	const [activeFeature, setActiveFeature] = React.useState(0);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onFeatureSelect = useCallback(
		(featureKey: number) => {
			setActiveFeature(featureKey);
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'tab',
				actionSubjectId: 'featuresHighlightTab',
				source: 'featuresHighlight',
				attributes: {
					label: `feature ${featureKey + 1}`,
					tabIndex: featureKey,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const singleFeature = (content: React.ReactNode, featureKey: number) => (
		<div
			css={css({
				cursor: 'pointer',
				position: 'relative', // needed for absolutely-positioned blue line child
				[media.above.md]: {
					marginBlock: token('space.200'),
				},
				[media.above.sm]: {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
					'&:hover h3': {
						color: wacToken('color.primary.blue'),
					},
				},
				[media.below.md]: {
					gridColumn: 'span 4',
				},
				[media.below.sm]: {
					// width: `calc(${contentWidth}px - (2 * ${WCL_GRID_PADDING_SM}px))`,
					display: 'block',
				},
				':focus-visible': {
					outline: `2px solid ${wacToken('color.primary.blue')}`,
					outlineOffset: '4px',
				},
			})}
			onClick={() => onFeatureSelect(featureKey)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onFeatureSelect(featureKey);
				}
			}}
			className={`feature ${activeFeature === featureKey ? 'selected' : ''}`}
			role="tab"
			tabIndex={0}
		>
			{activeFeature === featureKey && (
				<div
					css={css({
						position: 'absolute',
						zIndex: 1,
						backgroundColor: wacToken('color.primary.blue'),
						borderRadius: '4px',
						width: '100%',
						height: '8px',
						[media.above.md]: {
							height: '100%',
							width: '8px',
						},
					})}
				/>
			)}
			<div
				css={css({
					[media.above.md]: {
						paddingInline: token('space.200'),
					},
					[media.below.md]: {
						padding: token('space.200'),
					},
				})}
			>
				{content}
			</div>
		</div>
	);

	const feature1Text = (
		<ContentBlock
			title={props.feature1Title}
			titleAs="h3"
			text={props.feature1Body}
			eyebrow={props.feature1Eyebrow}
			alignment="start"
		/>
	);

	const feature1Image = (
		<Picture
			image={{
				src: props.feature1ImageSrc,
				alt: props.feature1ImageAltText ?? '',
				style: { width: '100%', height: 'auto' },
			}}
			style={{ width: '100%', height: 'auto' }}
		/>
	);

	const feature1Video = props.feature1VideoSrc ? (
		<Video
			key="feature1-video"
			src={props.feature1VideoSrc}
			poster={props.feature1VideoPoster}
			alt={props.feature1VideoAltText ?? ''}
		/>
	) : null;
	const feature2Video = props.feature2VideoSrc ? (
		<Video
			key="feature2-video"
			src={props.feature2VideoSrc}
			poster={props.feature2VideoPoster}
			alt={props.feature2VideoAltText ?? ''}
		/>
	) : null;
	const feature3Video = props.feature3VideoSrc ? (
		<Video
			key="feature3-video"
			src={props.feature3VideoSrc}
			poster={props.feature3VideoPoster}
			alt={props.feature3VideoAltText ?? ''}
		/>
	) : null;
	const feature2Text = (
		<ContentBlock
			title={props.feature2Title}
			titleAs="h3"
			text={props.feature2Body}
			eyebrow={props.feature2Eyebrow}
			alignment="start"
		/>
	);
	const feature2Image = (
		<Picture
			image={{
				src: props.feature2ImageSrc,
				alt: props.feature2ImageAltText ?? '',
				style: { width: '100%', height: 'auto' },
			}}
			style={{ width: '100%', height: 'auto' }}
		/>
	);
	const feature3Text = (
		<ContentBlock
			title={props.feature3Title}
			titleAs="h3"
			text={props.feature3Body}
			eyebrow={props.feature3Eyebrow}
			alignment="start"
		/>
	);
	const feature3Image = (
		<Picture
			image={{
				src: props.feature3ImageSrc,
				alt: props.feature3ImageAltText ?? '',
				style: { width: '100%', height: 'auto' },
			}}
			style={{ width: '100%', height: 'auto' }}
		/>
	);

	const featureTexts = [feature1Text, feature2Text, props.feature3Title && feature3Text];
	const content = [
		props.feature1ImageSrc ? feature1Image : feature1Video,
		props.feature2ImageSrc ? feature2Image : feature2Video,
		props.feature3Title && props.feature3ImageSrc ? feature3Image : feature3Video,
	];

	const sectionContainerStyles = css({
		[media.above.sm]: {
			margin: 'auto',
			paddingBlock: token('space.600'),
			maxWidth: '100%',
			width: '100%',
			paddingInline: `${WCL_GRID_PADDING_LG}px`,
			gridTemplateColumns: 'repeat(12, 1fr)',
			display: 'grid',
			boxSizing: 'border-box',
			gap: 'var(--cl-gap)',
			'--cl-gap': '24px',
		},
		[media.above.md]: {
			paddingInline: `${WCL_GRID_PADDING_LG}px`,
			maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`,
			'--cl-gap': '32px',
		},
		[media.below.sm]: {
			display: 'flex',
			flexDirection: 'column',
			width: '100%',
			height: '100%',
			gap: token('space.200'),
			paddingBlock: token('space.500'),
		},
	});

	const topContentContainerStyles = css({
		[media.below.sm]: {
			width: '100%',
			paddingBlockEnd: token('space.300'),
			paddingInline: `${WCL_GRID_PADDING_SM}px`,
		},
		[media.below.md]: {
			gridColumn: '2 / span 10',
		},
		[media.above.md]: {
			gridColumn: '3 / span 8',
		},
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		alignItems: 'center',
	});

	const featuresContainerStyles = css({
		[media.above.md]: {
			gridColumn: 'span 4',
			display: 'flex',
			flexDirection: 'column',
		},
		[media.only.sm]: {
			display: 'contents',
		},
		display: 'none',
	});

	const imageContainerStyles = css({
		[media.above.md]: {
			gridColumn: 'span 8',
			height: '100%',
		},
		[media.below.md]: {
			gridColumn: 'span 12',
		},
		[media.below.sm]: {
			display: 'none',
		},
	});

	const navDotsContainerStyles = css({
		display: 'flex',
		gap: token('space.200'),
		justifyContent: 'center',
		paddingBlockStart: token('space.200'),
	});

	const navDot = (featureNum: number) => {
		return (
			<div
				css={css({
					width: '8px',
					height: '8px',
					borderRadius: '4px',
					backgroundColor:
						featureNum === activeFeature
							? wacToken('color.primary.black')
							: wacToken('color.neutral.light500'),
				})}
			/>
		);
	};

	const navDotsContainer = () => (
		<div css={navDotsContainerStyles}>{featureTexts.map((_, index) => navDot(index))}</div>
	);

	const mobileCarouselContainerStyles = css({
		display: 'none',
		[media.below.sm]: {
			display: 'block',
		},
	});

	const sliderSettings: SlickSettings = {
		dots: true,
		infinite: false,
		slidesToShow: 1,
		slidesToScroll: 1,
		arrows: false,
		afterChange: (current) => setActiveFeature(current),
		appendDots: navDotsContainer,
	};

	const featureSlideStyles = css({
		paddingInline: `${WCL_GRID_PADDING_SM}px`,
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	});

	return (
		<section css={sectionContainerStyles}>
			<div css={topContentContainerStyles}>
				<ContentBlock
					title={props.title}
					titleAs="h2"
					text={props.description}
					alignment="center"
				/>
				{props.linkText && props.linkHref && (
					<Link text={props.linkText} href={props.linkHref} aria-label={props.linkText} />
				)}
			</div>
			<div css={featuresContainerStyles} role="tablist">
				{featureTexts.map((feature, index) => singleFeature(feature, index))}
			</div>
			<div css={imageContainerStyles} role="tabpanel">
				<div key={activeFeature}>{content[activeFeature]}</div>
			</div>
			<div css={[mobileCarouselContainerStyles, slickStyle]} className="slider-container">
				<Slider {...sliderSettings}>
					{featureTexts.map((feature, index) => (
						<div key={index} css={featureSlideStyles}>
							{singleFeature(feature, index)}
							{content[index]}
						</div>
					))}
				</Slider>
			</div>
		</section>
	);
};

// Source https://github.com/kenwheeler/slick/blob/master/slick/slick.css
const slickStyle = css({
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slider': {
		position: 'relative',
		display: 'block',
		boxSizing: 'border-box',
		WebkitUserSelect: 'none',
		MozUserSelect: 'none',
		MsUserSelect: 'none',
		userSelect: 'none',
		WebkitTouchCallout: 'none',
		KhtmlUserSelect: 'none',
		MsTouchAction: 'pan-y',
		touchAction: 'pan-y',
		WebkitTapHighlightColor: 'transparent',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-list': {
		position: 'relative',
		display: 'block',
		overflow: 'hidden',
		margin: 0,
		padding: 0,
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-list:focus': {
		outline: 'none',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-list.dragging': {
		cursor: 'pointer',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slider .slick-track, .slick-slider .slick-list': {
		WebkitTransform: 'translate3d(0, 0, 0)',
		MozTransform: 'translate3d(0, 0, 0)',
		MsTransform: 'translate3d(0, 0, 0)',
		OTransform: 'translate3d(0, 0, 0)',
		transform: 'translate3d(0, 0, 0)',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-track': {
		position: 'relative',
		insetBlockStart: 0,
		insetInlineStart: 0,
		display: 'block',
		marginInlineStart: 'auto',
		marginInlineEnd: 'auto',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-track:before, .slick-track:after': {
		display: 'table',
		content: "''",
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-track:after': {
		clear: 'both',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-loading .slick-track': {
		visibility: 'hidden',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide': {
		display: 'none',
		float: 'left',
		height: '100%',
		minHeight: '1px',
	},

	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide > div': {
		display: 'flex',
		justifyContent: 'center',
	},

	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[dir='rtl'] .slick-slide": {
		float: 'right',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide img': {
		display: 'block',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide.slick-loading img': {
		display: 'none',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-slide.dragging img': {
		pointerEvents: 'none',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-initialized .slick-slide': {
		display: 'block',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-loading .slick-slide': {
		visibility: 'hidden',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-vertical .slick-slide': {
		display: 'block',
		height: 'auto',
		border: '1px solid transparent',
	},
	// nested selector for third-party component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.slick-arrow.slick-hidden': {
		display: 'none',
	},
});
